import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

/**
 * A scheduled/recurring ("cron") job for the clinic (BE-1784/BE-1785): the agent
 * or a sweep re-runs its `payload` on a cadence — either a fixed
 * `intervalMinutes` or a `cron` expression. The admin panel lists these and lets
 * staff edit the cadence, pause/resume, end, or create new ones.
 *
 * Last-run timestamp/status and the "run now" trigger come from BE-1906.
 */
export type LastRunStatus = components["schemas"]["RecurrenceRunStatus"];

export interface Recurrence {
  id: string;
  title: string;
  payload: unknown | null;
  intervalMinutes: number | null;
  cron: string | null;
  nextDueAt: string;
  startsAt: string;
  endsAt: string | null;
  pausedAt: string | null;
  lastRunAt: string | null;
  lastRunStatus: LastRunStatus | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecurrenceInput {
  title: string;
  payload?: unknown | null;
  intervalMinutes?: number | null;
  cron?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
}

export const toRecurrence = (
  r: components["schemas"]["RecurrenceOut"],
): Recurrence => ({
  id: r.uuid,
  title: r.title,
  payload: r.payload,
  intervalMinutes: r.interval_minutes,
  cron: r.cron,
  nextDueAt: r.next_due_at,
  startsAt: r.starts_at,
  endsAt: r.ends_at,
  pausedAt: r.paused_at,
  lastRunAt: r.last_run_at,
  lastRunStatus: r.last_run_status,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

const toCreateBody = (
  input: RecurrenceInput,
  about: string[],
): components["schemas"]["RecurrenceCreate"] => ({
  title: input.title,
  about,
  payload: input.payload ?? null,
  interval_minutes: input.intervalMinutes ?? null,
  cron: input.cron ?? null,
  starts_at: input.startsAt ?? null,
  ends_at: input.endsAt ?? null,
});

const toUpdateBody = (
  input: RecurrenceInput,
): components["schemas"]["RecurrenceUpdate"] => ({
  title: input.title,
  payload: input.payload ?? null,
  interval_minutes: input.intervalMinutes ?? null,
  cron: input.cron ?? null,
  starts_at: input.startsAt ?? null,
  ends_at: input.endsAt ?? null,
});

export const recurrencesClient = {
  list: (subject?: string): Promise<Recurrence[]> =>
    request(() =>
      apiClient.GET("/recurrences", {
        params: { query: subject ? { subject } : {} },
      }),
    ).then((rows) => rows.map(toRecurrence)),

  get: (id: string): Promise<Recurrence> =>
    request(() =>
      apiClient.GET("/recurrences/{record_uuid}", {
        params: { path: { record_uuid: id } },
      }),
    ).then(toRecurrence),

  // Admin creates are clinic-wide — there's no entity picker here, so `about` is
  // the clinic uuid. The agent creates subject-linked recurrences in chat.
  create: (input: RecurrenceInput, about: string[]): Promise<Recurrence> =>
    request(() =>
      apiClient.POST("/recurrences", { body: toCreateBody(input, about) }),
    ).then(toRecurrence),

  update: (id: string, input: RecurrenceInput): Promise<Recurrence> =>
    request(() =>
      apiClient.PATCH("/recurrences/{recurrence_uuid}", {
        params: { path: { recurrence_uuid: id } },
        body: toUpdateBody(input),
      }),
    ).then(toRecurrence),

  pause: (id: string): Promise<Recurrence> =>
    request(() =>
      apiClient.POST("/recurrences/{recurrence_uuid}/pause", {
        params: { path: { recurrence_uuid: id } },
      }),
    ).then(toRecurrence),

  resume: (id: string): Promise<Recurrence> =>
    request(() =>
      apiClient.POST("/recurrences/{recurrence_uuid}/resume", {
        params: { path: { recurrence_uuid: id } },
      }),
    ).then(toRecurrence),

  end: (id: string): Promise<Recurrence> =>
    request(() =>
      apiClient.POST("/recurrences/{recurrence_uuid}/end", {
        params: { path: { recurrence_uuid: id } },
      }),
    ).then(toRecurrence),

  runNow: (id: string): Promise<Recurrence> =>
    request(() =>
      apiClient.POST("/recurrences/{recurrence_uuid}/run", {
        params: { path: { recurrence_uuid: id } },
      }),
    ).then(toRecurrence),
};
